import React, { useEffect, useState } from "react";
import axios from "axios";
import { Form, Modal } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { showToast } from "../../utils/ToastHelper";
import { BASE_URL } from "../Const/Url";
import { GC_API, searchInvoices, showApiError } from "./giftCardCommon";

const emptyForm = {
  expiry_date: "",
  invoice: "",
  issued_to_name: "",
  issued_to_email: "",
  issued_to_mobile: "",
};

// Shared by the card list and the batch list's "cards in batch" view, so the
// activate flow stays identical wherever it's launched from.
const BulkActivateModal = ({ show, ids, onHide, onDone }) => {
  const [form, setForm] = useState(emptyForm);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (show) {
      setForm(emptyForm);
      setSelectedInvoice(null);
      setResult(null);
    }
  }, [show]);

  const pickInvoice = (option) => {
    setSelectedInvoice(option);
    if (!option) {
      setForm((f) => ({ ...f, invoice: "" }));
      return;
    }
    axios
      .get(`${BASE_URL}api/v1/sales/inventory/invoice/${option.value}/`)
      .then((res) => {
        const inv = res.data?.data || {};
        setForm((f) => ({
          ...f,
          invoice: inv.id ?? option.invoice?.id ?? "",
          issued_to_name: inv.bill_to || f.issued_to_name,
          issued_to_mobile: inv.to_mobile || f.issued_to_mobile,
          issued_to_email: inv.to_email || f.issued_to_email,
        }));
      })
      .catch(() => showToast("error", "Could not read that invoice"));
  };

  const activate = () => {
    const payload = { ids };
    Object.entries(form).forEach(([key, value]) => {
      if (value !== "")
        payload[key] = key === "invoice" ? Number(value) : value;
    });
    setBusy(true);
    axios
      .post(`${GC_API}admin/card/activate-bulk/`, payload)
      .then((res) => {
        // Some admin endpoints wrap the payload in {data: ...}, this one may not.
        const data = res.data?.data ?? res.data ?? {};
        setResult(data);
        showToast(
          "success",
          `Activated ${data.activated_count || 0} of ${ids.length} card(s)`
        );
        if (onDone) onDone(data);
      })
      .catch((err) => showApiError(err))
      .finally(() => setBusy(false));
  };

  const label = `${ids.length} Card${ids.length === 1 ? "" : "s"}`;

  return (
    <Modal
      centered
      dialogClassName="gc-modal"
      show={show}
      onHide={() => !busy && onHide()}
    >
      <Modal.Header>
        <Modal.Title>Activate {label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <div>
            <p className="gc-note">
              Activated {result.activated_count || 0} of{" "}
              {(result.activated_count || 0) + (result.failed_count || 0)}{" "}
              card(s).
            </p>
            {result.failed?.length > 0 && (
              <ul className="gc-hint" style={{ paddingLeft: "18px", margin: 0 }}>
                {result.failed.map((f) => (
                  <li key={f.id}>
                    Card #{f.id}: {f.detail || f.code}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            <p className="gc-hint" style={{ margin: "0 0 16px" }}>
              Every field is optional and applies to all {label.toLowerCase()}.
              Leave the expiry blank for cards that never expire.
            </p>
            <div className="gc-form-grid">
              <div>
                <label className="gc-label" htmlFor="gc-bulk-expiry">
                  Expiry Date
                </label>
                <Form.Control
                  id="gc-bulk-expiry"
                  className="gc-input"
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) =>
                    setForm({ ...form, expiry_date: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="gc-label" htmlFor="gc-bulk-invoice">
                  Invoice
                </label>
                <AsyncSelect
                  inputId="gc-bulk-invoice"
                  isClearable
                  cacheOptions
                  value={selectedInvoice}
                  onChange={pickInvoice}
                  loadOptions={searchInvoices}
                  placeholder="Search invoice number or customer"
                  maxMenuHeight={220}
                  // the modal clips its own overflow, so the list goes to the body
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.trim().length < 2
                      ? "Type at least 2 characters"
                      : "No invoice found"
                  }
                />
              </div>
              <div>
                <label className="gc-label" htmlFor="gc-bulk-name">
                  Issued To Name
                </label>
                <Form.Control
                  id="gc-bulk-name"
                  className="gc-input"
                  value={form.issued_to_name}
                  onChange={(e) =>
                    setForm({ ...form, issued_to_name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="gc-label" htmlFor="gc-bulk-mobile">
                  Issued To Mobile
                </label>
                <Form.Control
                  id="gc-bulk-mobile"
                  className="gc-input"
                  placeholder="+8801700000000"
                  value={form.issued_to_mobile}
                  onChange={(e) =>
                    setForm({ ...form, issued_to_mobile: e.target.value })
                  }
                />
              </div>
              <div className="gc-form-grid--full">
                <label className="gc-label" htmlFor="gc-bulk-email">
                  Issued To Email
                </label>
                <Form.Control
                  id="gc-bulk-email"
                  className="gc-input"
                  type="email"
                  value={form.issued_to_email}
                  onChange={(e) =>
                    setForm({ ...form, issued_to_email: e.target.value })
                  }
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <button className="gc-btn" onClick={onHide}>
            Close
          </button>
        ) : (
          <>
            <button
              className="gc-btn gc-btn--ghost"
              disabled={busy}
              onClick={onHide}
            >
              Cancel
            </button>
            <button className="gc-btn" disabled={busy} onClick={activate}>
              {busy ? "Activating…" : `Activate ${label}`}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkActivateModal;
